import "react-international-phone/style.css";

import {
  BaseTextFieldProps,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";

export interface MUIPhoneProps extends BaseTextFieldProps {
  value: string;
  onChange: (phone: string) => void;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  countryChange?: (country: any) => void;
  errorMsg?: string;
}

const countries = defaultCountries.filter((country) => {
  const { iso2 } = parseCountry(country);
  return ["us", "gb", "ie"].includes(iso2);
});

const PhoneInput: React.FC<MUIPhoneProps> = ({
  value,
  onChange,
  label,
  required,
  disabled = false,
  placeholder,
  countryChange = () => {},
  errorMsg = "",
  ...restProps
}) => {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: "us",
      value,
      countries: countries,
      onChange: (data) => {
        countryChange(data.country);
        onChange(data.phone);
      },
    });

  return (
    <FormControl variant="standard" className={"w-full"}>
      <div className={"flex justify-between mb-[10px] text-xs"}>
        <p
          className={"text-primaryText text-left"}
          style={{
            fontFamily: "Helvetica",
            lineHeight: "1.4375em !important",
          }}
        >
          {label}
        </p>
        {errorMsg ? (
          <p className={"text-red-500 text-right mr-2"}>
            {errorMsg}
          </p>
        ) : null}
      </div>
      <TextField
        variant="outlined"
        color="primary"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => {
          handlePhoneValueChange(e);
        }}
        type="tel"
        inputRef={inputRef}
        sx={{ "& fieldset": { border: "none" }, input: { color: "#fff" } }}
        style={{
          backgroundColor: "#242A2F",
          width: "100%",
          color: "#fff",
          borderRadius: 10,
          border: "1px solid #242A2F",
          fontSize: 14,
          outline: "none",
          height: "54px",
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              style={{ marginRight: "2px", marginLeft: "-8px" }}
            >
              <Select
                MenuProps={{
                  style: {
                    height: "300px",
                    width: "360px",
                    top: "10px",
                    left: "-34px",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: {
                    sx: {
                      width: "max-content",
                    },
                  },
                }}
                sx={{
                  width: "max-content",
                  fieldset: {
                    display: "none",
                  },
                  '&.Mui-focused:has(div[aria-expanded="false"])': {
                    fieldset: {
                      display: "block",
                    },
                  },
                  // Update default spacing
                  ".MuiSelect-select": {
                    padding: "8px",
                    paddingRight: "24px !important",
                  },
                  svg: {
                    right: 0,
                    color: "#fff",
                  },
                }}
                value={country.iso2}
                onChange={(e) => setCountry(e.target.value as CountryIso2)}
                renderValue={(value) => (
                  <FlagImage iso2={value} style={{ display: "flex" }} />
                )}
              >
                {countries.map((c) => {
                  const country = parseCountry(c);
                  return (
                    <MenuItem key={country.iso2} value={country.iso2}>
                      <FlagImage
                        iso2={country.iso2}
                        style={{ marginRight: "8px" }}
                      />
                      <Typography marginRight="8px">{country.name}</Typography>
                      <Typography color="gray">+{country.dialCode}</Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </InputAdornment>
          ),
        }}
        required={required}
        disabled={disabled}
        {...restProps}
      />
    </FormControl>
  );
};

export default PhoneInput;
